import React, { useEffect, useState } from 'react'
import { Box, IconButton, Typography, Grid, Card, Input, Autocomplete } from '@mui/material';
import axios from 'axios';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import StopIcon from '@mui/icons-material/Stop';
import { DataGridPremium, GridActionsCellItem, useGridApiRef, useKeepGroupedColumnsHidden } from '@mui/x-data-grid-premium';
import moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StackedBarChartCard from './kron/StackedBarChartCard';
import TimeProfileLineChartCard from './kron/TimeProfileLineChartCard';
import { GRID_SAMPLE_DATE_COL_VAL } from 'layouts/SampleDataGrid';

function OngoingSession({children}) {
  return (
    <Grid size={{ xs: 12 }}>
      <Card sx={{ px: 2, py: 1 }}>
        {children}
      </Card>
    </Grid>
  )
}

const GRID_DATETIME_COL_DEF = {
  type: 'dateTime',
  width: 180,
  editable: true,
  groupable: false,
  // groupingValueGetter: value => {
  //   return moment(value).startOf('day').toDate()
  // }
}

export default function Kron() {
  const [ongoing, setOngoing] = useState([])
  const [completed, setCompleted] = useState(null)
  const [newSessionName, setNewSessionName] = useState('')
  const [newSessionName_input, setNewSessionName_input] = useState('')
  const [autocompleteOptions, setAutocompleteOptions] = useState([])
  const [aggregationModel, setAggregationModel] = useState({})
  useEffect(() => {
    axios.get('/api/public/kron/autocomplete_names').then(({data}) => {
      setAutocompleteOptions(data.map(x => x.name))
    })
  }, [])
  useEffect(() => {
    axios.get('/api/public/kron/read').then(({data : {ongoing, completed}}) => {
      setOngoing(ongoing)
      setCompleted(completed)
    })
  }, [])
  const handleStartSession = (e) => {
    e.preventDefault()
    _postStartSession(newSessionName)
  }
  const _postStartSession = (name) => {
    const newId = Math.random().toString(36).substring(2, 15)
    axios.post('/api/public/kron/start', { name })
      .then(({data : {new_data}}) => {
        setOngoing(prev => prev.map(x => x.id === newId ? {...x, ...new_data} : x))
      })
    setOngoing(prev => [...prev, { id: newId, name, time_start: new Date().toISOString() }])
    setNewSessionName('')
    setNewSessionName_input('')
  }
  const handleStopSession = (id) => {
    axios.post('/api/public/kron/stop', { id })
      .then(({data : {updated_data}}) => {
        setCompleted(prev => [updated_data, ...prev])
      })
    setOngoing(prev => prev.filter(x => x.id !== id))
  }
  const handleDelete = (id) => {
    axios.post('/api/public/kron/delete', { id })
    setCompleted(prev => prev.filter(x => x.id !== id))
  }
  const apiRef = useGridApiRef()
  useKeepGroupedColumnsHidden({apiRef})
  return (
    <Box padding={2} sx={{width: '100%'}}>
      <Typography component='h2' variant='h6' color='textSecondary' gutterBottom>
        Ongoing
      </Typography>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <OngoingSession>
            <form onSubmit={handleStartSession} style={{ display: 'flex', alignItems: 'center' }}>
              <Autocomplete
                freeSolo
                aria-label='Name of new session'
                disableClearable
                blurOnSelect={true}
                options={autocompleteOptions}
                value={newSessionName}
                inputValue={newSessionName_input}
                onInputChange={(_, value) => setNewSessionName_input(value)}
                onChange={(_, value, reason) => {
                  if (reason === 'selectOption' || reason === 'createOption')
                    _postStartSession(value)
                }}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <Input
                    {...params.slotProps.input}
                    inputRef={params.slotProps.input.ref}
                    inputProps={params.slotProps.htmlInput}
                    disableUnderline
                    fullWidth
                    placeholder="Start a new session..."
                    startAdornment={<DirectionsRunIcon sx={{ mr: 1, color: 'gray' }} />}
                  />
                )}
              />
              <IconButton
                onClick={() => _postStartSession(newSessionName_input)}
                disabled={!newSessionName_input.trim()}
                sx={{ visibility: newSessionName_input.trim() ? 'visible' : 'hidden' }}
                aria-label='Start session'
              >
                <PlayArrowIcon />
              </IconButton>
            </form>
        </OngoingSession>
        {ongoing.map((session) => (
          <OngoingSession key={session.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography>{session.name}</Typography>
                <Typography variant='body2' color='gray'>
                  Since {moment(session.time_start).format('h:mma')}, {moment(session.time_start).fromNow()}
                </Typography>
              </Box>
              <IconButton onClick={() => handleStopSession(session.id)} aria-label='Stop session'>
                <StopIcon />
              </IconButton>
            </Box>
          </OngoingSession>
        ))}
      </Grid>
      <Typography component='h2' variant='h6' color='textSecondary' gutterBottom>
        Insights
      </Typography>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <StackedBarChartCard />
        <TimeProfileLineChartCard />
      </Grid>
      <Typography component='h2' variant='h6' color='textSecondary' gutterBottom>
        Details
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 500,
          pb: 2,
        }}
      >
        <DataGridPremium
          apiRef={apiRef}
          density='compact'
          rows={completed || []}
          loading={completed === null}
          aggregationModel={aggregationModel}
          onAggregationModelChange={(newModel) => setAggregationModel(newModel)}
          onRowGroupingModelChange={(newModel) => {
            const newAggregationModel = Object.fromEntries(
              Object.entries({
                duration: 'sum',
                time_start: 'min',
                time_stop: 'max',
              }).filter(([key]) => !newModel.includes(key))
            )
            setAggregationModel(newAggregationModel)
          }}
          groupingColDef={{ flex: 1 }}
          columns={[
            { field: 'name', headerName: 'Name', flex: 1, minWidth: 200, editable: true },
            { field: 'duration',
              headerName: 'Duration',
              type: 'number',
              width: 90,
              valueGetter: (_, row) => moment.duration(moment(row.time_stop).diff(moment(row.time_start))).asMinutes(),
              valueFormatter: (value) => {
                const duration = moment.duration({ minutes: value })
                const hours = Math.floor(duration.asHours())
                const minutes = duration.minutes()
                if (duration.asMinutes() < 1) {
                  return '<1m'
                }
                return `${hours > 0 ? hours + 'h ' : ''}${minutes}m`
              },
              align: 'right',
              groupable: false,
            },
            { field: 'time_start', headerName: 'Start', ...GRID_DATETIME_COL_DEF, ...GRID_SAMPLE_DATE_COL_VAL('time_start', 'M/D/YYYY h:mma') },
            { field: 'time_stop', headerName: 'Stop', ...GRID_DATETIME_COL_DEF, ...GRID_SAMPLE_DATE_COL_VAL('time_stop', 'M/D/YYYY h:mma') },
            { field: 'actions', type: 'actions', headerName: '', width: 50, getActions: (params) => params.row.id ? [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDelete(params.id)}
                color='inherit'
              />
            ] : []},
          ]}
          hideFooter
          processRowUpdate={(newRow) => {
            axios.post('/api/public/kron/update', { ...newRow })
              .then(({ data : { updated_data }}) => {
                setCompleted(prev => prev.map(x => x.id === newRow.id ? updated_data : x))
              })
            return newRow
          }}
        />
      </Box>
    </Box>
  )
}
